/**
 * 2days 워크보드 데이터 모양 — "2days project · MVP 확정본 v1.3" 의 테이블·상태 정의를 따른다.
 * 서버 응답도 이 모양으로 받는다고 가정한다 (snake_case → camelCase 만 다르다).
 *
 * Workboard
 *   project   { id, title, teamName, startAt(PM 시작 = T0), endAt(T0 + 49h) }
 *   members   [{ id, name, job(planner·designer·developer·marketer·npc_lead),
 *                role(leader·member·npc) — 리더는 팀에 1명. 기본은 기획자(O1 기본 지정 규칙),
 *                status(active·dropped), timezone(IANA), availableFrom/To('HH:mm', 본인 시간대) }]
 *   tasks     [{ id, title, assigneeId, receiverId, dueAt, isBlocked,
 *                state(assigned·declined·in_progress·submitted·rework_requested·approved·cancelled),
 *                assignedAt, acceptedAt?, expectedSubmitAt?,
 *                approvedSubmittedAt?(승인된 버전의 handoff_submitted 시각),
 *                predecessorIds[](handoffs.from_task_id → to_task_id), returnWaitMinutes? }]
 *   (task 추가 필드) taskList[](Task List — 받는 사람이 항목별 ✓/✗), files[{ name, kind, size, url? }],
 *                contributors[{ userId, reason, commentId?, addedBy }], declineReason?, deliverable?
 *   comments  [{ id, taskId, authorId, type(question·blocker·general), targetUserId?, body, createdAt, parentId }]
 *   checkpoints [{ id, type(requested·general), name, scheduledAt, createdBy, targetJobs[](general) }]
 *   checkins    [{ checkpointId, userId, checkedInAt, note, updatedAt? }]
 *   notifications [{ id, userId(받는 사람), text, link{ to: task·checkpoints·complete-project, id?, commentId? }, createdAt, readAt,
 *                    kind(task_assigned · handoff_submitted · comment_mentioned …), actorId(행동한 사람 · npc · null) }]
 *   completeProject  동료 평가 열림·마감 (model/completeProject.js)
 *   (task) plannedStartAt? — 리더가 타임라인에서 옮긴 계획 시작. 없으면 수락(배정) 시각
 *   viewerId  지금 보고 있는 사람
 *   frozenNow 목 데이터 전용 — 화면 시각을 고정한다. 실제 응답에는 없다
 *
 * 시각은 모두 UTC ISO.
 */

/**
 * 파일럿 #1 목 데이터 — 2days 백엔드가 생기기 전까지 화면을 띄우는 용도.
 * 디자인 시안(Workboard Prototype)과 같은 장면: Leo Tran 시점, 10/16 11:20 KST.
 * 시각은 KST 로 적고 UTC ISO 로 바꿔 둔다 — 서버도 UTC 로 준다.
 */
const kst = (day, hh, mm = 0) =>
  new Date(Date.UTC(2026, 9, day, hh - 9, mm)).toISOString();

export const PILOT_PROJECT_ID = 'pilot-1';

export const pilotWorkboard = {
  project: {
    id: PILOT_PROJECT_ID,
    title: '2days · Pilot #1',
    teamName: 'Team Bori',
    // 브리프 시나리오 이름 (briefs.scenario) — 모달 부제로 쓴다
    briefTitle: 'Delivery app: the last 100 m',
    // 브리프 시나리오 · 결과물 — 첫 진입 시나리오 모달과 Resources › Scenario
    scenario:
      'Foreign residents in Seoul order food in English, but riders lose minutes in the last 100 m: the wrong building gate, no door code, or a note they can’t read.\n\nYour team builds the first version of a delivery note flow: the customer adds clear drop-off details in English, and the rider sees them in Korean on one screen. Keep it small enough to ship in 2 days.',
    deliverable:
      'A working web link of the delivery note flow (customer → rider screen), the PRD and QA report, and a one-page summary of what you built and left out.',
    // PM 시작 = T0. 팀원은 1시간 뒤, 종료는 T0 + 49h (MVP §8-8)
    startAt: kst(15, 9),
    endAt: kst(17, 10),
  },
  viewerId: 'lt',
  frozenNow: kst(16, 11, 20),
  // requested 체크포인트 — 팀원 시작(T0 + 1h) 기준 6시간마다 7개 자동 생성. 운영진만 수정, 삭제 불가
  // general 체크포인트 — 리더가 직군을 골라 만든다. 체크인이 0건일 때만 삭제
  checkpoints: [
    ...[17, 23, 29, 35, 41, 47, 53].map((h, i) => ({
      id: `cp${i + 1}`,
      type: 'requested',
      name: `Checkpoint ${i + 1}`,
      scheduledAt: kst(15, h),
      createdBy: 'ops',
      targetJobs: null,
    })),
    {
      id: 'g1',
      type: 'general',
      name: 'Design review',
      scheduledAt: kst(16, 15),
      createdBy: 'mp',
      targetJobs: ['designer', 'planner'],
    },
    {
      id: 'g2',
      type: 'general',
      name: 'Launch copy review',
      scheduledAt: kst(16, 20),
      createdBy: 'mp',
      targetJobs: ['marketer', 'planner'],
    },
  ],
  // 태스크 댓글 (MVP §7). type: question · blocker · general. 질문·막힘은 받는 사람을 지정한다
  comments: [
    {
      id: 'c1',
      taskId: 't4',
      authorId: 'lt',
      type: 'blocker',
      targetUserId: 'ar',
      body: 'The rider screen needs the gate and door-code fields from the API. Which fields will the order response include?',
      createdAt: kst(16, 11, 5),
      parentId: null,
      reactions: { '👀': ['dk'], '👍': ['mp'] },
    },
    {
      id: 'c2',
      taskId: 't3',
      authorId: 'ar',
      type: 'question',
      targetUserId: 'lt',
      body: 'Do the time slot chips need a disabled style for past slots? I don’t see one in the specs.',
      createdAt: kst(16, 11, 15),
      parentId: null,
    },
    {
      id: 'c3',
      taskId: 't4',
      authorId: 'mp',
      type: 'general',
      targetUserId: null,
      body: '@Leo Tran can you share the seat map frame before lunch? Daniel needs it for the booking UI.',
      mentionIds: ['lt'],
      createdAt: kst(16, 10, 50),
      parentId: null,
    },
    {
      id: 'c4',
      taskId: 't4',
      authorId: 'ar',
      type: 'reply',
      targetUserId: null,
      body: 'It returns seatId, floor, zone, status and heldUntil. I’ll post a sample response here.',
      createdAt: kst(16, 11, 12),
      parentId: 'c1',
    },
    {
      id: 'c5',
      taskId: 't7',
      authorId: 'mp',
      type: 'rejection',
      targetUserId: 'dk',
      body: 'The seed orders have no gate or door-code fields, so the rider screen shows empty notes.',
      reason: 'missing_items',
      detail: 'Marked ✗: Seed data from sample_orders.json',
      createdAt: kst(16, 9, 10),
      parentId: null,
    },
  ],
  // 체크인은 팀 전체에 공개된다. 지금(10/16 11:20) 기준:
  //  CP1 늦은 2명(AR·DK) · CP2 모두 제때 · CP3 DK 놓침, LT·AR·MP 는 오프라인 복귀 후 30분 안 (On time)
  //  CP4 Leo 놓침 → Check in late, Daniel 은 아직 오프라인이라 복귀 후 30분까지 열려 있다
  checkins: [
    { checkpointId: 'cp1', userId: 'mp', checkedInAt: kst(15, 16, 30), note: 'Spec and screen list handed off.' },
    { checkpointId: 'cp1', userId: 'lt', checkedInAt: kst(15, 16, 40), note: 'Component specs started, buttons and inputs done.' },
    { checkpointId: 'cp1', userId: 'ar', checkedInAt: kst(15, 17, 20), note: 'API outline drafted.' },
    { checkpointId: 'cp1', userId: 'dk', checkedInAt: kst(15, 17, 10), note: 'Repo and CI set up.' },
    { checkpointId: 'cp2', userId: 'lt', checkedInAt: kst(15, 22, 40), note: 'Specs 70%. Customer note flow next.' },
    { checkpointId: 'cp2', userId: 'mp', checkedInAt: kst(15, 22, 50), note: 'Reviewed v1 specs.' },
    { checkpointId: 'cp2', userId: 'ar', checkedInAt: kst(15, 22, 30), note: 'Order and address endpoints sketched.' },
    { checkpointId: 'cp2', userId: 'dk', checkedInAt: kst(15, 22, 45), note: 'Test env needs a DB fix.' },
    { checkpointId: 'cp3', userId: 'mp', checkedInAt: kst(16, 8, 10), note: 'Approved the screen list. QA plan next.' },
    { checkpointId: 'cp3', userId: 'lt', checkedInAt: kst(16, 8, 15), note: 'Specs submitted. Starting the customer note screens.' },
    { checkpointId: 'cp3', userId: 'ar', checkedInAt: kst(16, 11, 5), note: 'Picking up the order API today.' },
    { checkpointId: 'cp4', userId: 'mp', checkedInAt: kst(16, 10, 55), note: 'QA plan ready. Watching the API.' },
    { checkpointId: 'cp4', userId: 'ar', checkedInAt: kst(16, 10, 58), note: 'Started the order API.' },
  ],
  // 알림 목록 (#23). 받는 사람별
  // 보는 사람 본인 — Pre-join 에서 동의한 항목 (Settings › View your consent)
  me: {
    email: 'leotran@example.com',
    consentPeerReview: true,
    consentDataUse: true,
    consentOverseasTransfer: true,
    consentPromoUse: true,
    consentSelfCheckLink: true,
    consentTermsVersion: 'v1.3',
    nameDisplay: 'real',
    consentedAt: '2026-10-14T09:00:00+09:00',
  },
  // kind · actorId — Inbox 가 Needs action · Activity 로 나눈다. 멘션 · 답글 · 반응은 시드
  notifications: [
    {
      id: 'n1',
      userId: 'lt',
      text: 'Mina Park assigned you “Design empty and error states”',
      link: { to: 'task', id: 't5' },
      createdAt: kst(16, 11, 5),
      readAt: null,
      kind: 'task_assigned',
      actorId: 'mp',
    },
    {
      id: 'n2',
      userId: 'lt',
      text: '“Design review” is at Oct 16, 15:00 KST',
      link: { to: 'checkpoints' },
      createdAt: kst(16, 9),
      readAt: kst(16, 9, 30),
      kind: 'checkpoint_notice',
      actorId: null,
    },
    {
      id: 'n3',
      userId: 'mp',
      text: 'Aisha Rahman checked in to Checkpoint 4',
      link: { to: 'checkpoints' },
      createdAt: kst(16, 11, 10),
      readAt: null,
      kind: 'checked_in',
      actorId: 'ar',
    },
    {
      id: 'n4',
      userId: 'mp',
      text: 'Leo Tran moved the expected submit time for “Design delivery note screens” to Oct 16, 15:00 KST',
      link: { to: 'task', id: 't4' },
      createdAt: kst(16, 10, 40),
      readAt: null,
      kind: 'expected_changed',
      actorId: 'lt',
    },
    {
      id: 'n5',
      userId: 'lt',
      text: 'Mina Park handed off “Write feature spec” for your review',
      link: { to: 'task', id: 't1' },
      createdAt: kst(15, 19, 30),
      readAt: kst(15, 19, 35),
      kind: 'handoff_submitted',
      actorId: 'mp',
    },
    {
      id: 'n6',
      userId: 'lt',
      text: 'Mina Park handed off “Define screen list and copy” for your review',
      link: { to: 'task', id: 't2' },
      createdAt: kst(15, 19, 40),
      readAt: kst(15, 19, 45),
      kind: 'handoff_submitted',
      actorId: 'mp',
    },
    {
      id: 'n7',
      userId: 'lt',
      text: 'Aisha Rahman asked you a question on “Design component specs”',
      link: { to: 'task', id: 't3', commentId: 'c2' },
      createdAt: kst(16, 11, 15),
      readAt: null,
      kind: 'question_asked',
      actorId: 'ar',
    },
    {
      id: 'n8',
      userId: 'lt',
      text: 'Mina Park mentioned you on “Design booking flow screens”',
      link: { to: 'task', id: 't4', commentId: 'c3' },
      createdAt: kst(16, 10, 50),
      readAt: null,
      kind: 'comment_mentioned',
      actorId: 'mp',
    },
    {
      id: 'n9',
      userId: 'lt',
      text: 'Aisha Rahman replied to your comment on “Design booking flow screens”',
      link: { to: 'task', id: 't4', commentId: 'c4' },
      createdAt: kst(16, 11, 12),
      readAt: null,
      kind: 'comment_replied',
      actorId: 'ar',
    },
    {
      id: 'n10',
      userId: 'lt',
      text: 'Daniel Kim reacted 👀 to your comment',
      link: { to: 'task', id: 't4', commentId: 'c1' },
      createdAt: kst(16, 11, 8),
      readAt: kst(16, 11, 9),
      kind: 'comment_reacted',
      actorId: 'dk',
    },
    {
      id: 'n11',
      userId: 'lt',
      text: 'Mina Park reacted 👍 to your comment',
      link: { to: 'task', id: 't4', commentId: 'c1' },
      createdAt: kst(16, 11, 14),
      readAt: null,
      kind: 'comment_reacted',
      actorId: 'mp',
    },
    {
      id: 'n12',
      userId: 'lt',
      text: 'Mina Park changed the Task List of “Design booking flow screens” · Added error states',
      link: { to: 'task', id: 't4' },
      createdAt: kst(16, 9, 40),
      readAt: kst(16, 9, 45),
      kind: 'scope_changed',
      actorId: 'mp',
    },
    {
      id: 'n13',
      userId: 'mp',
      text: 'Daniel Kim declined “Build search filter” · Not my area',
      link: { to: 'task', id: 't8' },
      createdAt: kst(16, 2, 20),
      readAt: kst(16, 8),
      kind: 'task_declined',
      actorId: 'dk',
    },
  ],

  members: [
    {
      id: 'mp',
      lastSeenAt: kst(16, 11, 19),
      name: 'Mina Park',
      job: 'planner',
      role: 'leader',
      status: 'active',
      timezone: 'Asia/Seoul',
      availableFrom: '08:00',
      availableTo: '00:00',
    },
    {
      id: 'lt',
      name: 'Leo Tran',
      job: 'designer',
      role: 'member',
      status: 'active',
      timezone: 'Asia/Ho_Chi_Minh',
      availableFrom: '06:00',
      availableTo: '21:00',
    },
    {
      id: 'ar',
      lastSeenAt: kst(16, 11, 5),
      name: 'Aisha Rahman',
      job: 'developer',
      role: 'member',
      status: 'active',
      timezone: 'Asia/Dhaka',
      availableFrom: '08:00',
      availableTo: '00:00',
    },
    {
      id: 'dk',
      lastSeenAt: kst(16, 8, 20),
      name: 'Daniel Kim',
      job: 'developer',
      role: 'member',
      status: 'active',
      timezone: 'Europe/Berlin',
      availableFrom: '09:00',
      availableTo: '23:00',
    },
    {
      id: 'sr',
      name: 'Sofia Reyes',
      job: 'marketer',
      role: 'member',
      status: 'dropped',
      timezone: 'America/Mexico_City',
      availableFrom: '09:00',
      availableTo: '18:00',
    },
    {
      id: 'tk',
      name: 'Team lead Kim',
      job: 'npc_lead',
      role: 'npc',
      status: 'active',
      timezone: 'Asia/Seoul',
      availableFrom: '09:00',
      availableTo: '18:00',
    },
  ],
  tasks: [
    {
      id: 't1',
      title: 'Write feature spec',
      assigneeId: 'mp',
      receiverId: 'lt',
      dueAt: kst(15, 18),
      state: 'approved',
      isBlocked: false,
      assignedAt: kst(15, 9),
      acceptedAt: kst(15, 9, 10),
      // 1시간 30분 늦게 냈다 → 뒷사람(Design delivery note screens) 기한이 그만큼 밀린다
      approvedSubmittedAt: kst(15, 19, 30),
      deliverable: {
        link: 'https://www.notion.so/team-bori/delivery-note-prd',
        links: ['https://www.notion.so/team-bori/delivery-note-prd'],
        linkKinds: ['PRD'],
        slots: [{ k: 'prd', l: 'PRD', field: 'prd_url', url: 'https://www.notion.so/team-bori/delivery-note-prd' }],
        files: [],
        summary: 'Problem, users, success metric and the note rules for the delivery note flow.',
        submittedAt: kst(15, 19, 30),
        submittedBy: 'mp',
      },
      review: { result: 'approved', at: kst(15, 19, 50) },
      taskList: ['Problem, users and success metric', 'Note rules: gate, door code, drop-off spot, 200 characters max'],
      files: [],
      contributors: [],
      predecessorIds: [],
    },
    {
      id: 't2',
      title: 'Define screen list and copy',
      assigneeId: 'mp',
      receiverId: 'lt',
      dueAt: kst(15, 20),
      state: 'approved',
      isBlocked: false,
      assignedAt: kst(15, 9),
      acceptedAt: kst(15, 9, 15),
      approvedSubmittedAt: kst(15, 19, 40),
      deliverable: {
        link: 'https://www.notion.so/team-bori/screen-list-and-copy',
        links: ['https://www.notion.so/team-bori/screen-list-and-copy'],
        linkKinds: ['PRD'],
        slots: [{ k: 'prd', l: 'PRD', field: 'prd_url', url: 'https://www.notion.so/team-bori/screen-list-and-copy' }],
        files: [],
        summary: 'Six screens for customer and rider, with final copy for every button and error.',
        submittedAt: kst(15, 19, 40),
        submittedBy: 'mp',
      },
      review: { result: 'approved', at: kst(15, 20, 5) },
      taskList: ['List of 6 screens (customer and rider)', 'Final copy for every button and error'],
      files: [],
      contributors: [],
      predecessorIds: [],
    },
    {
      id: 't3',
      title: 'Design component specs',
      assigneeId: 'lt',
      receiverId: 'ar',
      dueAt: kst(16, 12),
      state: 'submitted',
      isBlocked: false,
      assignedAt: kst(15, 10),
      acceptedAt: kst(16, 8),
      deliverable: {
        link: 'https://example.com/component-specs',
        summary: 'Specs for buttons, inputs, address cards and note chips, with spacing and color tokens.',
        submittedAt: kst(16, 11),
      },
      taskList: ['Buttons, inputs, address card and note chip specs', 'Spacing and color tokens noted'],
      files: [],
      contributors: [],
      predecessorIds: ['t2'],
    },
    {
      id: 't4',
      title: 'Design delivery note screens',
      assigneeId: 'lt',
      receiverId: 'ar',
      dueAt: kst(16, 14),
      state: 'in_progress',
      isBlocked: true,
      assignedAt: kst(15, 10),
      acceptedAt: kst(16, 9, 30),
      expectedSubmitAt: kst(16, 15),
      activity: [
        { type: 'scope', at: kst(16, 9, 40), by: 'leader', reason: 'Added error states' },
        { type: 'expected', at: kst(16, 10, 40), by: 'lt', from: kst(16, 13), to: kst(16, 15) },
      ],
      taskList: ['6 screens: order list, address, add note, confirm, rider view (Korean), delivered', 'Default, empty and error state for each screen', 'Uses the shared component specs'],
      files: [{ name: 'delivery-note-brief.pdf', kind: 'PDF', size: '1.2 MB' }, { name: 'rider-screen-reference.png', kind: 'Image', size: '840 KB' }],
      contributors: [],
      predecessorIds: ['t1'],
    },
    {
      id: 't5',
      title: 'Design empty and error states',
      assigneeId: 'lt',
      receiverId: 'ar',
      dueAt: kst(16, 18),
      state: 'assigned',
      isBlocked: false,
      assignedAt: kst(16, 11, 5),
      // Leo 가 이미 열어봤다 → 빨간 점 없음. QA delivery note flow(Mina)는 아직 안 열어봐서 점이 남는다
      openedAt: kst(16, 11, 12),
      taskList: ['Empty, loading and error states for the 6 screens', 'Follows the component specs'],
      files: [{ name: 'state-copy.docx', kind: 'Word document', size: '47 KB' }],
      contributors: [],
      predecessorIds: ['t2'],
    },
    {
      id: 't6',
      title: 'Build order and note API',
      assigneeId: 'ar',
      receiverId: 'mp',
      dueAt: kst(16, 20),
      state: 'in_progress',
      isBlocked: false,
      assignedAt: kst(15, 10),
      acceptedAt: kst(16, 11),
      taskList: ['Endpoints for orders, addresses and delivery notes', 'Gate and door-code fields in the order response'],
      files: [],
      contributors: [],
      predecessorIds: ['t3'],
    },
    {
      id: 't7',
      title: 'Set up test environment',
      assigneeId: 'dk',
      receiverId: 'mp',
      dueAt: kst(16, 16),
      state: 'rework_requested',
      isBlocked: false,
      assignedAt: kst(15, 10),
      acceptedAt: kst(16, 0, 30),
      expectedSubmitAt: kst(16, 14),
      deliverable: {
        link: 'https://staging.team-bori.dev',
        links: ['https://staging.team-bori.dev', 'https://github.com/team-bori/delivery-note'],
        linkKinds: ['Working web link', 'GitHub repository'],
        slots: [
          { k: 'web', l: 'Working web link', field: 'web_url', url: 'https://staging.team-bori.dev' },
          { k: 'repo', l: 'GitHub repository', field: 'repo_url', url: 'https://github.com/team-bori/delivery-note' },
        ],
        files: [{ name: 'seed-log.txt', kind: 'TXT', size: '4 KB' }],
        summary: 'Staging is up with CI. Seed script loads sample orders.',
        submittedAt: kst(16, 8, 40),
        submittedBy: 'dk',
      },
      review: {
        result: 'rejected',
        failedItems: ['Seed data from sample_orders.json'],
        reason: 'missing_items',
        description: 'The seed orders have no gate or door-code fields, so the rider screen shows empty notes.',
        at: kst(16, 9, 10),
      },
      taskList: ['Staging URL the team can open', 'Seed data from sample_orders.json'],
      files: [],
      contributors: [],
      predecessorIds: [],
    },
    {
      id: 't8',
      title: 'Build note translation',
      assigneeId: 'dk',
      receiverId: 'mp',
      dueAt: kst(16, 22),
      state: 'declined',
      isBlocked: false,
      assignedAt: kst(16, 2),
      declineReason: 'Not my area',
      activity: [{ type: 'decline', at: kst(16, 2, 20), by: 'dk', reason: 'Not my area' }],
      taskList: ['Show the English note in Korean on the rider screen'],
      files: [],
      contributors: [],
      predecessorIds: ['t2'],
    },
    {
      id: 't9',
      title: 'Build customer and rider screens',
      assigneeId: 'dk',
      receiverId: 'mp',
      dueAt: kst(16, 23),
      state: 'assigned',
      isBlocked: false,
      assignedAt: kst(16, 10, 30),
      taskList: ['Add note, confirm and rider screens built from v1 designs', 'Connected to the order API'],
      files: [],
      contributors: [],
      predecessorIds: ['t4'],
    },
    {
      id: 't10',
      title: 'QA delivery note flow',
      assigneeId: 'mp',
      receiverId: 'tk',
      dueAt: kst(17, 8),
      state: 'assigned',
      isBlocked: false,
      assignedAt: kst(16, 11),
      taskList: ['Every customer → rider path tested', 'Known bugs listed'],
      files: [],
      contributors: [],
      predecessorIds: ['t6', 't9'],
    },
    {
      // 브리프 범위를 줄이며 운영진이 취소한 태스크
      id: 't11',
      title: 'Design ops dashboard',
      assigneeId: 'lt',
      receiverId: 'ar',
      dueAt: kst(16, 20),
      state: 'cancelled',
      isBlocked: false,
      assignedAt: kst(15, 10),
      taskList: ['Ops view of today’s late deliveries'],
      files: [],
      contributors: [],
      predecessorIds: [],
    },
  ],
};
